"use client";

import { type InputHTMLAttributes, type ReactNode, forwardRef, useId } from "react";

interface FloatingInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: ReactNode;
  error?: string;
  /** Optional inline action rendered after the error text (e.g. a "Yes" apply-suggestion button). */
  errorAction?: ReactNode;
}

export const FloatingInput = forwardRef<HTMLInputElement, FloatingInputProps>(
  ({ label, error, errorAction, className, id, value, ...props }, ref) => {
    const autoId = useId();
    const inputId = id ?? (typeof label === "string" ? label.toLowerCase().replace(/\s+/g, "-") : autoId);
    const hasValue = value !== undefined && value !== "";

    return (
      <div className="relative">
        <style>{`
          .floating-input:-webkit-autofill ~ .floating-label {
            top: 0.375rem !important;
            transform: translateY(0) !important;
            font-size: 0.7rem !important;
          }
        `}</style>
        <input
          ref={ref}
          id={inputId}
          placeholder=" "
          value={value}
          className={`
            floating-input peer w-full px-3.5 pt-5 pb-2 text-[0.95rem] border rounded-[var(--radius-lg)]
            bg-white outline-none transition-colors duration-200
            ${error ? "border-[var(--color-danger)]" : "border-[var(--color-gray-300)] focus:border-[var(--color-accent)]"}
            ${className ?? ""}
          `}
          {...props}
        />
        <label
          htmlFor={inputId}
          className={`
            floating-label absolute left-3.5 transition-all duration-200 pointer-events-none
            ${hasValue
              ? "top-1.5 -translate-y-0 text-[0.7rem]"
              : "peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-[0.95rem] top-1.5 -translate-y-0 text-[0.7rem]"
            }
            peer-focus:top-1.5 peer-focus:-translate-y-0 peer-focus:text-[0.7rem]
            ${error ? "text-[var(--color-danger)]" : "text-[var(--color-gray-400)] peer-focus:text-[var(--color-accent)]"}
          `}
        >
          {label}
        </label>
        {error && (
          <p className="text-[0.75rem] text-[var(--color-danger)] mt-1">
            {error}
            {errorAction}
          </p>
        )}
      </div>
    );
  }
);

FloatingInput.displayName = "FloatingInput";
